import React, { useState } from 'react';
import { Modal, Image, Tabs } from 'antd';
import {
  AppstoreOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import getImage from 'helpers/getImage';
import ImageGallery from '../image-gallery';
import { MediaLibraryPanel } from '../media-library-modal';
import { useTranslation } from 'react-i18next';

const MediaUpload = ({
  imageList = [],
  setImageList,
  form,
  type,
  multiple = true,
  name,
  disabled,
  text = 'upload',
  galleryBlogLink,
  onBlogCoverGallerySuccess,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [tab, setTab] = useState('upload');
  const showModal = () => {
    setFileList([]);
    setTab('upload');
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setLoadingBtn(false);
    setFileList([]);
  };

  /**
   * Take a pick from the library and put it in the same shape an upload takes.
   *
   * `ImageGallery` stores `{ uid, name, url }` where both name and url are the
   * asset's storage path, and everything downstream — the thumbnails here, the
   * `images` form field, whatever the form eventually submits — reads that
   * shape. A library pick arrives as a resolved absolute URL with the path
   * beside it, so this takes the path where there is one; a list where some
   * entries are paths and others are absolute URLs works until somebody moves
   * the asset host.
   */
  const addFromLibrary = (picked) => {
    const chosen = Array.isArray(picked) ? picked : [picked];
    const images = chosen
      .map((item) => item?.path || item?.url || '')
      .filter(Boolean)
      .map((path) => ({
        uid: path,
        name: path,
        status: 'done',
        url: path,
        created: true,
      }));
    if (!images.length) return;
    const current = imageList ?? [];
    /* Adding one the product already has would put the same thumbnail on it
       twice, and the second one is not removable independently. */
    const fresh = images.filter(
      (img) => !current.some((existing) => (existing?.url ?? existing) === img.url),
    );
    if (!fresh.length) {
      handleCancel();
      return;
    }
    const next = multiple ? [...current, ...fresh] : [fresh[0]];
    setImageList(next);
    form?.setFieldsValue?.({ images: next });
    handleCancel();
  };

  const removeImg = (path) => {
    const nextArray = imageList.filter((item) => item !== path);
    form?.setFieldsValue?.({
      images: nextArray,
    });
    setImageList(nextArray);
  };

  return (
    <>
      <div className='media-upload-wrapper'>
        {imageList
          ?.filter((item) => !item?.isVideo)
          .map((item, idx) => (
            <div
              key={idx}
              className='image-wrapper'
              onClick={() => (disabled ? undefined : removeImg(item))}
            >
              <Image
                preview={false}
                src={getImage(item?.name)}
                className='images'
                alt={'images'}
              />
              <DeleteOutlined color='white' hidden={disabled} />
            </div>
          ))}
        {(multiple || !imageList.length) && (
          <div className='media-upload' onClick={showModal}>
            <PlusOutlined /> <span>{t(text)}</span>
          </div>
        )}
      </div>
      <Modal
        onCancel={handleCancel}
        maskClosable
        visible={isModalOpen}
        footer={null}
        /* Wide enough for the library grid on the second tab. It was 520,
           which is right for a drop zone and about two columns of thumbnails.
           The `--slim` modifier stays: it is what gives this modal its header,
           its padding and its shadow, and none of that depends on the width. */
        width={880}
        centered
        destroyOnClose
        className='media-upload-modal media-upload-modal--slim'
        title={t(text)}
      >
        {/*
          Upload, or take one that is already there.

          This was upload-only, and the library was reachable from the variant
          picker but not from here — so the same photo got uploaded again every
          time it was needed on a second product, and the library filled up with
          copies. The panel is the same one the variant picker uses, in picker
          mode, so both places browse the same library the same way and neither
          has its own idea of what a folder is.
        */}
        <Tabs activeKey={tab} onChange={setTab} className='media-upload-modal__tabs'>
          <Tabs.TabPane
            key='upload'
            tab={
              <span>
                <CloudUploadOutlined /> {t('upload')}
              </span>
            }
          >
            <div className='upload-media'>
              <ImageGallery
                type={type}
                form={form}
                setFileList={setImageList}
                fileList={imageList}
                multiple={multiple}
                loadingBtn={loadingBtn}
                setLoadingBtn={setLoadingBtn}
                imageList={fileList}
                setImageList={setFileList}
                handleClose={handleCancel}
                galleryBlogLink={galleryBlogLink}
                onBlogCoverGallerySuccess={onBlogCoverGallerySuccess}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane
            key='library'
            tab={
              <span>
                <AppstoreOutlined /> {t('media.library.browse')}
              </span>
            }
          >
            <MediaLibraryPanel
              mode='picker'
              multiple={multiple}
              defaultFolder={String(type || '').trim() || undefined}
              onPick={addFromLibrary}
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </>
  );
};
export default MediaUpload;
